/**
 * Local adapter — playable with no backend. Persists in localStorage.
 * Used as fallback when the Orangecat API is unreachable, and for guest play.
 */

import type { AuthAdapter, AuthUser, LeaderboardQuery, SubmitResult } from "./types";
import type { Grade, LeaderboardRow, SubmitPayload } from "../types";
import { loadStore, saveStore, scoreFromEventsSafe } from "./localStoreBridge";

const USERS_KEY = "ft_local_users";

interface LocalUser {
  id: string;
  nickname: string;
  password: string;
}

function users(): LocalUser[] {
  try {
    return JSON.parse(localStorage.getItem(USERS_KEY) || "[]");
  } catch {
    return [];
  }
}
function saveUsers(u: LocalUser[]) {
  localStorage.setItem(USERS_KEY, JSON.stringify(u));
}

let session: AuthUser | null = null;

try {
  const raw = localStorage.getItem("ft_local_session");
  if (raw) session = JSON.parse(raw);
} catch {
  session = null;
}

export const localAdapter: AuthAdapter = {
  id: "local",

  async getSession() {
    return session;
  },

  async login(username, password) {
    const u = users().find((x) => x.nickname.toLowerCase() === username.toLowerCase() && x.password === password);
    if (!u) return { ok: false, error: "Invalid nickname or password." };
    session = { id: u.id, nickname: u.nickname, isParent: true, source: "local" };
    localStorage.setItem("ft_local_session", JSON.stringify(session));
    return { ok: true, user: session };
  },

  async register(username, password) {
    const nick = username.trim();
    if (nick.length < 2) return { ok: false, error: "Nickname must be at least 2 characters." };
    if (password.length < 6) return { ok: false, error: "Password must be at least 6 characters." };
    const all = users();
    if (all.some((x) => x.nickname.toLowerCase() === nick.toLowerCase())) {
      return { ok: false, error: "That nickname is taken." };
    }
    const u: LocalUser = { id: "loc_" + Date.now().toString(36), nickname: nick, password };
    all.push(u);
    saveUsers(all);
    session = { id: u.id, nickname: nick, isParent: true, source: "local" };
    localStorage.setItem("ft_local_session", JSON.stringify(session));
    return { ok: true, user: session };
  },

  async logout() {
    session = null;
    localStorage.removeItem("ft_local_session");
  },

  async submitScore(payload: SubmitPayload): Promise<SubmitResult> {
    const computed = scoreFromEventsSafe(payload);
    if (!computed.ok) return { ok: false, rejected: true, error: computed.error };
    const s = loadStore();
    const nick = session?.nickname || payload.nickname || "Guest";
    if (payload.mode !== "practice") {
      s.localScores.push({
        mode: payload.mode,
        grade: payload.grade,
        score: computed.score!,
        timeMs: payload.timeMs,
        stars: payload.stars,
        date: new Date().toISOString(),
        nickname: nick,
        misses: payload.misses,
      });
      s.localScores = s.localScores.slice(-200);
    }
    saveStore(s);
    if (!session && payload.mode === "daily") {
      return { ok: false, needsAuth: true, score: computed.score, error: "Sign in to post on the Daily board." };
    }
    return { ok: true, score: computed.score };
  },

  async getLeaderboard(q: LeaderboardQuery): Promise<LeaderboardRow[]> {
    const s = loadStore();
    let rows = s.localScores.filter((r) => r.grade === q.grade);
    if (q.board === "daily") {
      const today = new Date().toISOString().slice(0, 10);
      rows = rows.filter((r) => r.mode === "daily" && r.date.slice(0, 10) === today);
    } else if (q.board === "campaign") {
      rows = rows.filter((r) => r.mode === "campaign");
    }
    rows.sort((a, b) => b.score - a.score || a.timeMs - b.timeMs || a.misses - b.misses);
    const seen = new Set<string>();
    const uniq: typeof rows = [];
    for (const r of rows) {
      if (seen.has(r.nickname)) continue;
      seen.add(r.nickname);
      uniq.push(r);
    }
    return uniq.slice(0, 50).map((r, i) => ({
      rank: i + 1,
      nickname: r.nickname,
      grade: r.grade as Grade,
      score: r.score,
      timeMs: r.timeMs,
      stars: r.stars,
      date: r.date.slice(0, 10),
      misses: r.misses,
    }));
  },

  async getProgress() {
    return null;
  },
};
